import type { UserProfile, UserProfilePreferencesForm } from "@shared/schemas";

import {
  BillingAddressCard,
  PreferencesHeader,
  ProfileInfoSection,
  RoasterInfoSection,
  ShippingAddressCard,
} from "@bcc/components/preferences";
import { usePermissions } from "@bcc/hooks/use-permissions";
import { useToast } from "@bcc/hooks/use-toast";
import { userProfileLanguageOptions } from "@client-shared/lib";
import { useSFDCAuthState } from "@client-shared/providers/auth";
import { useUserProfileState } from "@client-shared/providers/user-profile";
import useVendorContext from "@client-shared/providers/vendor";
import { zodResolver } from "@hookform/resolvers/zod";
import { createUserProfilePreferencesFormSchema, preferencesValidationKeyPrefix, UserProfileRequestSchema } from "@shared/schemas";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type FieldErrors, type Path, type Resolver, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { setProfilePreferencesTestId, splitStreetAddress } from "./profile-preferences.utils";

const logoMaxFileSizeBytes = 5 * 1024 * 1024;
const logoMimeTypePattern = /^image\/(png|jpeg|jpg|gif|tiff|webp)$/;
const resolverWithProvince = zodResolver(createUserProfilePreferencesFormSchema(true));
const resolverWithoutProvince = zodResolver(createUserProfilePreferencesFormSchema(false));
const fieldUpdateOptions = { shouldDirty: true, shouldTouch: true } as const;
const shippingCountryFields: Path<UserProfilePreferencesForm>[] = [
  "shippingAddress.country",
  "shippingAddress.countryCode",
  "shippingAddress.province",
  "shippingAddress.provinceCode",
];
const shippingProvinceFields: Path<UserProfilePreferencesForm>[] = ["shippingAddress.province", "shippingAddress.provinceCode"];
const getFirstErrorPath = (errorObject: unknown, parentPath = ""): string | undefined => {
  if (!errorObject || typeof errorObject !== "object") {
    return undefined;
  }

  const entries = Object.entries(errorObject as Record<string, unknown>);
  for (const [key, value] of entries) {
    if (key === "ref") {
      continue;
    }

    const currentPath = parentPath ? `${parentPath}.${key}` : key;
    if (value && typeof value === "object" && "message" in (value as Record<string, unknown>)) {
      return currentPath;
    }

    const nestedPath = getFirstErrorPath(value, currentPath);
    if (nestedPath) {
      return nestedPath;
    }
  }

  return undefined;
};

const combineAddress = (address1: string, address2: string): string => {
  if (!address2) return address1;
  return `${address1} ${address2}`;
};

const getDefaultValues = (profile: UserProfile | undefined): UserProfilePreferencesForm => ({
  userId: profile?.userId ?? "",
  name: profile?.name ?? "",
  firstName: profile?.firstName ?? "",
  lastName: profile?.lastName ?? "",
  phone: profile?.phone ?? "",
  email: profile?.email ?? "",
  languageLocaleKey: profile?.languageLocaleKey ?? "en",
  vendorLogoUrl: profile?.vendorLogoUrl,
  shippingAddress: {
    firstName: profile?.shippingAddress?.firstName ?? "",
    address1: combineAddress(profile?.shippingAddress?.address1 ?? "", profile?.shippingAddress?.address2 ?? ""),
    address2: profile?.shippingAddress?.address2 ?? "",
    city: profile?.shippingAddress?.city ?? "",
    provinceCode: profile?.shippingAddress?.provinceCode ?? "",
    country: profile?.shippingAddress?.country ?? "",
    countryCode: profile?.shippingAddress?.countryCode ?? "",
    zip: profile?.shippingAddress?.zip ?? "",
    phone: profile?.shippingAddress?.phone ?? "",
    latitude: profile?.shippingAddress?.latitude,
    longitude: profile?.shippingAddress?.longitude,
  },
});

const buildUserProfileRequest = (values: UserProfilePreferencesForm) => {
  const { address1, ...shippingAddressRest } = values.shippingAddress ?? {};
  const splitAddress = splitStreetAddress(address1);

  return UserProfileRequestSchema.parse({
    ...values,
    shippingAddress: {
      ...shippingAddressRest,
      address1: splitAddress.address1,
      address2: splitAddress.address2,
    },
  });
};

export function ProfilePreferences() {
  const [{ isLoading, isSaving, profile }, , { updateProfile, refreshProfile, uploadLogo }] = useUserProfileState();
  const [{ role }] = useSFDCAuthState();
  const [vendorState] = useVendorContext();
  const countries = useMemo(() => vendorState.countries ?? [], [vendorState.countries]);
  const { t } = useTranslation();
  const { toast } = useToast();
  const [logoPreview, setLogoPreview] = useState<string | null>(profile?.vendorLogoUrl || null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const [uploadError, setUploadError] = useState<string | null>(null);
  const defaultValues = useMemo(() => getDefaultValues(profile), [profile]);

  const { canEdit } = usePermissions();
  const isEditable = canEdit("settings");

  const hasStatesRef = useRef(false);
  const formResolver = useCallback<Resolver<UserProfilePreferencesForm>>(
    (...args) => (hasStatesRef.current ? resolverWithProvince(...args) : resolverWithoutProvince(...args)),
    []
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    setFocus,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<UserProfilePreferencesForm>({
    mode: "onTouched",
    resolver: formResolver,
    defaultValues,
  });

  useEffect(() => {
    void refreshProfile();
  }, [refreshProfile]);

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  useEffect(() => {
    setLogoPreview(profile?.vendorLogoUrl || null);
  }, [profile?.vendorLogoUrl]);

  // eslint-disable-next-line react-hooks/incompatible-library -- react-hook-form's watch is a subscription API not analyzable by React Compiler
  const selectedCountryCode = watch("shippingAddress.countryCode");
  const countriesByCode = useMemo(() => new Map(countries.map((country) => [country.CountryCode, country])), [countries]);

  const states = useMemo(() => {
    const country = countriesByCode.get(selectedCountryCode ?? "");
    return country?.States ?? [];
  }, [countriesByCode, selectedCountryCode]);

  const hasStates = states.length > 0;
  hasStatesRef.current = hasStates;

  useEffect(() => {
    if (!hasStates) {
      clearErrors(shippingProvinceFields);
    }
  }, [hasStates, clearErrors]);

  const getTranslatedErrorMessage = useCallback(
    (key?: string): string => {
      if (!key) {
        return "";
      }
      return t(`${preferencesValidationKeyPrefix}${key}`);
    },
    [t]
  );

  const handleLogoUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }

      if (file.size > logoMaxFileSizeBytes) {
        setUploadError(t("views.preferences.roasterInfo.fileSizeError"));
        setLogoFile(null);
        setLogoPreview(profile?.vendorLogoUrl || null);
        return;
      }

      if (!logoMimeTypePattern.test(file.type)) {
        setUploadError(t("views.preferences.roasterInfo.fileTypeError"));
        setLogoFile(null);
        setLogoPreview(profile?.vendorLogoUrl || null);
        return;
      }

      setUploadError(null);
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    },
    [t, profile?.vendorLogoUrl]
  );

  const handleSaveLogo = useCallback(async () => {
    if (!logoFile) {
      return;
    }
    setIsUploading(true);
    const success = await uploadLogo(logoFile);
    setIsUploading(false);
    if (success) {
      setLogoFile(null);
      toast({
        title: t("views.preferences.roasterInfo.logoSavedTitle"),
        description: t("views.preferences.roasterInfo.logoSavedDescription"),
      });
    } else {
      toast({
        title: t("views.preferences.roasterInfo.uploadErrorTitle"),
        description: t("views.preferences.roasterInfo.uploadErrorDescription"),
        variant: "destructive",
      });
    }
  }, [logoFile, uploadLogo, t, toast]);

  const handleCountryChange = useCallback(
    (countryCode: string) => {
      const countryName = countriesByCode.get(countryCode)?.Name;

      setValue("shippingAddress.countryCode", countryCode, fieldUpdateOptions);
      setValue("shippingAddress.country", countryName ?? "", fieldUpdateOptions);
      setValue("shippingAddress.province", "", fieldUpdateOptions);
      setValue("shippingAddress.provinceCode", "", fieldUpdateOptions);

      clearErrors(shippingCountryFields);
    },
    [clearErrors, countriesByCode, setValue]
  );

  const handleProvinceChange = useCallback(
    (province: string) => {
      setValue("shippingAddress.provinceCode", province, fieldUpdateOptions);
      clearErrors(shippingProvinceFields);
    },
    [clearErrors, setValue]
  );

  const handleInvalidSubmit = useCallback(
    (formErrors: FieldErrors<UserProfilePreferencesForm>) => {
      const firstErrorPath = getFirstErrorPath(formErrors);
      if (!firstErrorPath) {
        return;
      }
      setFocus(firstErrorPath as Path<UserProfilePreferencesForm>);
      const element = document.querySelector<HTMLElement>(`[name="${firstErrorPath}"]`);

      if (!element) {
        return;
      }
      if (typeof element.scrollIntoView === "function") {
        element.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
      window.setTimeout(() => {
        element.focus();
      }, 150);
    },
    [setFocus]
  );

  const handleValidSubmit = useCallback(
    async (values: UserProfilePreferencesForm) => {
      const payload = buildUserProfileRequest(values);
      const isUpdated = await updateProfile(payload);
      if (!isUpdated) {
        toast({
          title: t("views.preferences.profileInfo.saveErrorTitle"),
          description: t("views.preferences.profileInfo.saveErrorDescription"),
        });
        return;
      }
      toast({
        title: t("views.preferences.profileInfo.saveSuccessTitle"),
        description: t("views.preferences.profileInfo.saveSuccessDescription"),
      });
    },
    [toast, t, updateProfile]
  );

  return (
    <form
      data-testid={setProfilePreferencesTestId("container")}
      className="space-y-6"
      autoComplete="off"
      onSubmit={handleSubmit(handleValidSubmit, handleInvalidSubmit)}
    >
      <PreferencesHeader />

      <div className="grid gap-6">
        <RoasterInfoSection
          roasterNameValue={profile?.name || t("views.preferences.roasterInfo.roasterNameFallback")}
          logoPreview={logoPreview}
          uploadError={uploadError}
          control={control}
          isEditable={isEditable}
          isUploading={isUploading}
          hasPendingFile={logoFile !== null}
          onLogoUpload={handleLogoUpload}
          onSaveLogo={handleSaveLogo}
        />

        <BillingAddressCard billingAddress={profile?.billingAddress} />

        <ShippingAddressCard
          control={control}
          errors={errors}
          countries={countries}
          states={states}
          hasStates={hasStates}
          isEditable={isEditable}
          onCountryChange={handleCountryChange}
          onProvinceChange={handleProvinceChange}
          translateValidationError={getTranslatedErrorMessage}
        />

        <ProfileInfoSection
          role={role ?? "-"}
          isLoading={isLoading}
          isSaving={isSaving}
          control={control}
          errors={errors}
          isEditable={isEditable}
          languageOptions={userProfileLanguageOptions}
          translateValidationError={getTranslatedErrorMessage}
        />
      </div>
    </form>
  );
}
